import { useCallback, useLayoutEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { BIBLE_ENABLED, BIBLE_OVERVIEW_ENABLED } from '@/lib/feature-flags';
import { VoiceCaptureProvider } from '@/hooks/useVoiceCapture';
import PrayPage from '@/pages/PrayPage';
import SettingsPage from '@/pages/SettingsPage';
import BibleBookIndex from '@/pages/BibleBookIndex';
import BibleChapter from '@/pages/BibleChapter';
import BibleBookmarksPage from '@/pages/BibleBookmarksPage';
import JournalBookPage from '@/pages/JournalBookPage';
import JournalPassagePage from '@/pages/JournalPassagePage';
import JournalMeaningPage from '@/pages/JournalMeaningPage';
import JournalPrayedPage from '@/pages/JournalPrayedPage';
import ScriptureReflectionPage from '@/pages/ScriptureReflectionPage';
import BibleOverviewPage from '@/pages/BibleOverviewPage';
import BibleOverviewBookPage from '@/pages/BibleOverviewBookPage';
import { NavStackProvider } from '@/components/NavStackProvider';
import AuthGate from '@/components/AuthGate';
import OfflineReopenGate, { ReadingOnlyHome } from '@/components/OfflineReopenGate';
import AuthGateScreen, { AuthGateLoadingScene } from '@/components/AuthGateScreen';
import AccountLoadScreen from '@/components/AccountLoadScreen';
import OwnerRecoveryScreen from '@/components/OwnerRecoveryScreen';
import StorageUnreadableScreen from '@/components/StorageUnreadableScreen';
import OwnerScopedRuntime from '@/components/OwnerScopedRuntime';
import { useOwnerGate } from '@/hooks/useOwnerGate';
import { usePrayerEnrichment } from '@/hooks/usePrayerEnrichment';
import { usePrayerEnrichmentRetry } from '@/hooks/usePrayerEnrichmentRetry';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getSupabase } from '@/lib/supabase';
import { useUpdateHold } from '@/hooks/useUpdateHold';
import UpdateReadyLine from '@/components/UpdateReadyLine';
import TestimonyDraftNotice from '@/components/TestimonyDraftNotice';
import KeptRecordingNotice from '@/components/KeptRecordingNotice';
import UnsavedRecordingWarning from '@/components/UnsavedRecordingWarning';
import AppNoticeStack from '@/components/AppNoticeStack/AppNoticeStack';
import { AuthorizedReaderHost } from '@/components/ReaderRuntime';
import { getReadingAccessSnapshot, setReadingAdmission, useDefinitelyOffline, useReadingAccess } from '@/lib/reading-access';
import { getOfflineReopenSnapshot, subscribeOfflineReopen } from '@/lib/offline-reopen';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import { isOwnerTransitionPresentationBarrierActive } from '@/lib/owner-transition';
import { useNavStack } from '@/hooks/useNavLayer';
import { getRememberedOfflineOwnerId, useBaseRouteVisitKey, useOfflineBibleResume, useVerifiedHomeHandoff } from '@/hooks/useOfflineHomeNavigation';

// Invisible mount for the app-wide prayer enrichment loops. Isolated in
// its own leaf component so its store subscription re-renders only this
// null node, not the whole App tree, when prayers change. Hosts both the
// initial-parse worker (parsed === null rows) and the durable update-
// enrichment retry worker (pendingEnrichment tokens, offline-first Chunk 2).
function PrayerEnrichmentMount() {
  usePrayerEnrichment();
  usePrayerEnrichmentRetry();
  return null;
}

/**
 * Owner gate (core-content cutover). Renders the routed app ONLY when
 * the authenticated account is proven safe for this browser profile. Unbound
 * legacy content is replaced only after a complete authenticated server pull
 * validates. A different account follows the same replacement path only when
 * no positively identified unsynced work exists.
 *
 * `resolving` keeps authoring closed: deciding early is the exact failure
 * the gate exists to remove, and the window is one IDB read plus one session
 * read. Public BSB reading and a checked local snapshot have no authority to
 * mount the runtimes below.
 *
 * Sync, audio, keepsake dispatch, and enrichment mount only after this gate.
 * The placement is load-bearing once production writes are enabled.
 */
function OwnerRuntime({
  children,
  ownerId,
}: {
  children: ReactNode;
  ownerId: string;
}) {
  return (
    <VoiceCaptureProvider ownerId={ownerId}>
      <AuthorizedReaderHost ownerId={ownerId} />
      <OwnerScopedRuntime />
      <PrayerEnrichmentMount />
      {children}
      <TestimonyDraftNotice />
      <KeptRecordingNotice />
    </VoiceCaptureProvider>
  );
}

function LocalReadOnlyRuntime({ children }: { children: ReactNode }) {
  useLayoutEffect(() => setReadingAdmission('local-read-only',
    () => getSupabase() === null && isCoreReadOnly()), []);
  return <VoiceCaptureProvider>{children}</VoiceCaptureProvider>;
}

interface OwnerSetupFailure {
  retry: () => void;
  ownerId: string | null;
}

function AuthenticatedOwnerGate({ children, hideClosed, coldHandoffOwnerId, onHomeAction, onRetryChange }: {
  children: ReactNode; hideClosed: boolean; coldHandoffOwnerId: string | null;
  onHomeAction: (prayerId?: string) => void;
  onRetryChange: (failure: OwnerSetupFailure | null) => void;
}) {
  const gate = useOwnerGate();
  const { status, retry } = gate;
  const renderedAuthEpoch = getAuthEpoch();
  const readingAccess = useReadingAccess();
  const visitKey = useBaseRouteVisitKey();
  const [keptVisit, setKeptVisit] = useState<{ key: string; ownerId: string | null } | null>(null);
  // A retained cold Home owns the visible list while this gate is hidden.
  // Keep its manual recovery action connected to the ordinary owner retry.
  useLayoutEffect(() => {
    onRetryChange(status === 'connection-error' ? { retry, ownerId: gate.currentOwnerId } : null);
    return () => onRetryChange(null);
  }, [status, retry, gate.currentOwnerId, onRetryChange]);
  if (!hideClosed && status === 'connection-error' && keptVisit?.key !== visitKey) {
    setKeptVisit({ key: visitKey, ownerId: gate.currentOwnerId });
  }
  const retainedVisit = !hideClosed && keptVisit?.key === visitKey &&
    keptVisit.ownerId === gate.currentOwnerId &&
    (status === 'connection-error' || status === 'resolving' || status === 'ok');
  const homeHandoff = useVerifiedHomeHandoff(retainedVisit,
    status === 'ok' && !readingAccess.restricted && readingAccess.ownerId === gate.currentOwnerId, visitKey);
  const keepReading = retainedVisit && !homeHandoff;
  useLayoutEffect(() => {
    if (status !== 'ok' || !gate.currentOwnerId) return;
    const ownerId = gate.currentOwnerId;
    let disposed = false;
    let release: (() => void) | undefined;
    void waitForAuthResolution().then(() => {
      if (disposed || getAuthUserId() !== ownerId) return;
      const epoch = getAuthEpoch();
      release = setReadingAdmission(ownerId, () =>
        getAuthEpoch() === epoch && getAuthUserId() === ownerId &&
        !isOwnerTransitionPresentationBarrierActive());
    });
    return () => { disposed = true; release?.(); };
  }, [status, gate.currentOwnerId, renderedAuthEpoch]);
  // App-update safety: never reload while the account is being resolved or
  // an account switch is pending (the owner gate must finish first).
  useUpdateHold(status === 'resolving' || status === 'switch-pending', 'owner-gate');
  // AUTH_GATE_ENABLED is an eager presentation switch, not permission to paint
  // a cloud-bound profile while signed out. If the outer gate is disabled but
  // Supabase is configured, keep the owner boundary honest and show the same
  // sign-in surface instead of returning a permanently blank document.
  // Public BSB routes keep their own stable reader while this gate resolves.
  // Closed-account recovery/export components never mount behind that reader.
  if (hideClosed && status !== 'ok') return null;
  // Keep the admitted runtime in the same sibling slot when Home changes;
  // completing presentation must not restart sync or voice-provider custody.
  if (keepReading || (status === 'ok' && gate.currentOwnerId)) return <>
    {keepReading && <ReadingOnlyHome onHomeAction={onHomeAction}
      onRetry={status === 'connection-error' ? retry : undefined} />}
    {status === 'ok' && gate.currentOwnerId && <OwnerRuntime ownerId={gate.currentOwnerId}>
      {keepReading ? null : children}
    </OwnerRuntime>}
  </>;
  if (status === 'auth-required') {
    return getSupabase() ? (
      <AuthGateScreen phase="signin" />
    ) : (
      <AccountLoadScreen mode="connection-error" onRetry={retry} />
    );
  }
  if (status === 'resolving') {
    // A verified cold refresh still needs the ordinary owner bootstrap. Keep
    // its reading presentation until that settles, without granting access.
    if (coldHandoffOwnerId && (gate.currentOwnerId === null || gate.currentOwnerId === coldHandoffOwnerId)) {
      return <ReadingOnlyHome />;
    }
    return <AccountLoadScreen mode="loading" onRetry={retry} />;
  }
  if (status === 'unidentified-content') {
    return <OwnerRecoveryScreen mode="unidentified-content" />;
  }
  // Storage that could not be inspected is not content found: its own closed
  // screen, with Check again and Sign out only (rule #87).
  if (status === 'storage-unreadable') {
    return <StorageUnreadableScreen onCheckAgain={gate.checkStorageAgain} />;
  }
  if (status === 'client-update-required') {
    return (
      <AccountLoadScreen
        mode="client-update-required"
        onRetry={() => window.location.reload()}
      />
    );
  }
  if (status === 'connection-error') return <ReadingOnlyHome onRetry={retry} />;
  if (status === 'switch-pending') {
    return <AccountLoadScreen mode={status} onRetry={retry} />;
  }
  return null;
}

function OwnerGate({ children, hideClosed, coldHandoffOwnerId, onHomeAction, onRetryChange }: {
  children: ReactNode; hideClosed: boolean; coldHandoffOwnerId: string | null;
  onHomeAction: (prayerId?: string) => void;
  onRetryChange: (failure: OwnerSetupFailure | null) => void;
}) {
  // A backend-free read-only build is the deliberate local/offline fallback:
  // there is no cloud owner to resolve and every mutation remains blocked by
  // the shared core guard. This keeps the public Bible and already-local legacy
  // reads usable in development and reliability fixtures without weakening the
  // writable one-account-per-browser boundary. Owner-bearing sync, hydration,
  // audio processing, and enrichment loops remain unmounted in this fallback.
  if (getSupabase() === null && isCoreReadOnly()) {
    return <LocalReadOnlyRuntime>{children}</LocalReadOnlyRuntime>;
  }
  return <AuthenticatedOwnerGate hideClosed={hideClosed} coldHandoffOwnerId={coldHandoffOwnerId}
    onHomeAction={onHomeAction} onRetryChange={onRetryChange}>{children}</AuthenticatedOwnerGate>;
}

/** Only public reading routes bypass the account's presentation boundary. */
function isPublicBiblePath(pathname: string): boolean {
  if (!BIBLE_ENABLED) return false;
  return /^\/bible\/?$/.test(pathname) ||
    /^\/bible\/(?!bookmarks(?:\/|$))[^/]+\/\d+\/?$/.test(pathname) ||
    (BIBLE_OVERVIEW_ENABLED && /^\/bible\/overview(?:\/[^/]+)?\/?$/.test(pathname));
}

function PublicBibleRoutes() {
  return <Routes>
    <Route element={<Layout />}>
      <Route path="/bible" element={<BibleBookIndex />} />
      <Route path="/bible/:bookSlug/:chapter" element={<BibleChapter />} />
      {/* Bible overview (the map of Scripture): static `overview` prefix,
          flag-gated. RR ranks the static segment above `:bookSlug`, and no
          canonical slug equals "overview", so these routes cannot collide. */}
      {BIBLE_OVERVIEW_ENABLED && <>
        <Route path="/bible/overview" element={<BibleOverviewPage />} />
        <Route path="/bible/overview/:bookSlug" element={<BibleOverviewBookPage />} />
      </>}
    </Route>
  </Routes>;
}

interface HomePresentation {
  ownerId: string;
  authEpoch: number;
  prayerId?: string;
  focusWrite: boolean;
  fromKey: string;
  destinationKey?: string;
}

function ProtectedRoutes({ homePresentation }: { homePresentation: HomePresentation | null }) {
  return <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<PrayPage initialPresentation={homePresentation ?? undefined} />} />
      <Route path="/settings" element={<SettingsPage />} />
      {BIBLE_ENABLED ? <>
        <Route path="/bible/bookmarks" element={<BibleBookmarksPage />} />
        {/* Canon-spatial Journal pages (static `book/` prefix; the reflection
            route is unchanged and non-colliding: no canonical slug equals
            "book", and RR ranks the static segment above the dynamic one). */}
        <Route path="/bible/bookmarks/book/:bookSlug" element={<JournalBookPage />} />
        <Route path="/bible/bookmarks/book/:bookSlug/:chapter" element={<JournalPassagePage />} />
        {/* "Highlights by meaning" lens (v2-gated inside the page). Static
            `meaning` prefix; neither path collides with `book/...` or the
            three-segment reflection route. */}
        <Route path="/bible/bookmarks/meaning" element={<JournalMeaningPage />} />
        <Route path="/bible/bookmarks/meaning/:family" element={<JournalMeaningPage />} />
        {/* "Prayers on Scripture" lens (pray-gated inside the page).
            Static one-segment `prayed` prefix; collides with nothing. */}
        <Route path="/bible/bookmarks/prayed" element={<JournalPrayedPage />} />
        <Route path="/bible/bookmarks/:bookSlug/:chapter/:verse" element={<ScriptureReflectionPage />} />
      </> : <Route path="/bible/*" element={<Navigate to="/" replace />} />}
    </Route>
  </Routes>;
}

function AppRoutes() {
  const offline = useDefinitelyOffline();
  const nav = useNavStack();
  const signingIn = useSyncExternalStore(nav.subscribe, () => nav.has('gate-auth'));
  const location = useLocation();
  const navigate = useNavigate();
  const [homePresentation, setHomePresentation] = useState<HomePresentation | null>(null);
  const [ownerFailure, setOwnerFailure] = useState<OwnerSetupFailure | null>(null);
  const onRetryChange = useCallback((failure: OwnerSetupFailure | null) => setOwnerFailure(failure), []);
  const ownerRetry = ownerFailure?.retry ?? null;
  const failedOwnerId = ownerFailure?.ownerId ?? null;
  const access = useReadingAccess();
  const reopen = useSyncExternalStore(subscribeOfflineReopen, getOfflineReopenSnapshot, getOfflineReopenSnapshot);
  const restoringBible = useOfflineBibleResume(offline, reopen, access, signingIn);
  // A remembered token or a pending refresh is not a verified online session.
  // Only definite offline/presentation connection failure may show public BSB
  // before admission; neither permits any private read or runtime mount.
  const verifiedColdSession = reopen.verifiedOwnerId !== undefined &&
    getAuthUserId() === reopen.verifiedOwnerId;
  // The ordinary owner gate can fail after SDK session resolution without a
  // cold refresh (for example, unbound setup while the OS still says online).
  // Its existing connection-error view may offer public cached text only. This
  // is not precise transport evidence, private admission, or root-resume intent.
  const authUserId = getAuthUserId();
  const failedOwnerPublicReading = failedOwnerId !== null && reopen.phase === 'pass' &&
    (authUserId === undefined || authUserId === failedOwnerId) &&
    getRememberedOfflineOwnerId() === failedOwnerId;
  const readingAllowed = offline || reopen.offlineAtLaunch || reopen.presentationFailure !== undefined ||
    verifiedColdSession || failedOwnerPublicReading || !access.restricted;
  const checkingPublicBible = !signingIn && !readingAllowed && reopen.phase === 'checking' &&
    isPublicBiblePath(location.pathname);
  const hideProtected = restoringBible || checkingPublicBible;
  // A connection drop must not dismiss an email-code attempt already open.
  // Cold offline visits have no auth layer and enter reading immediately.
  const publicBible = !signingIn && readingAllowed && isPublicBiblePath(location.pathname);
  const onHomeAction = (prayerId?: string) => {
    const current = getReadingAccessSnapshot();
    if (isCoreReadOnly() || current.restricted || !current.ownerId || current.ownerId !== getAuthUserId()) return;
    setHomePresentation({ ownerId: current.ownerId, authEpoch: getAuthEpoch(),
      prayerId, focusWrite: prayerId === undefined, fromKey: location.key });
    navigate('/');
  };
  // Bind the one-shot request to the destination's first history entry.
  // The page captures it on mount; any later navigation drops it, so Back
  // cannot replay it. No request is stored in history or on disk.
  if (homePresentation && homePresentation.fromKey !== location.key) {
    if (!homePresentation.destinationKey) setHomePresentation({ ...homePresentation, destinationKey: location.key });
    else if (homePresentation.destinationKey !== location.key) setHomePresentation(null);
  }
  return <>
    {/* Resolve the fenced resume destination without painting the wrong Home. */}
    {hideProtected && <AuthGateLoadingScene />}
    {/* This stable sibling never changes ancestry when verification settles. */}
    {publicBible && <PublicBibleRoutes />}
    {/* Hard auth gate: blocks online signed-out routes. Inside NavStackProvider
        so its email modal can register `gate-auth` (rule #78). Enrichment
        remains inside the owner runtime, so no Grok calls run while gated. */}
    <OfflineReopenGate hideContent={publicBible || hideProtected} onHomeAction={onHomeAction} onOwnerRetry={ownerRetry ?? undefined}>
      {(renderProtected, coldHandoffOwnerId) => <AuthGate
        fallback={publicBible || !renderProtected || hideProtected ? null : undefined}
        loadingFallback={publicBible || !renderProtected || hideProtected ? null : coldHandoffOwnerId ? <ReadingOnlyHome /> : undefined}
      >
        <OwnerGate hideClosed={publicBible || !renderProtected || hideProtected} coldHandoffOwnerId={coldHandoffOwnerId}
          onHomeAction={onHomeAction} onRetryChange={onRetryChange}>
          {!publicBible && !hideProtected && renderProtected && <ProtectedRoutes homePresentation={homePresentation} />}
        </OwnerGate>
      </AuthGate>}
    </OfflineReopenGate>
    {/* Unsaved-recording warning (account-change recording plan rev 4.1,
        section 8): beside the gates, so it stays visible on the sign-in and
        account screens while this page still holds an unsaved recording.
        It names no account and shows no content. */}
    {/* App notice stack (testimony plan rev 4 section 32.2): stable slots for
        the warning, testimony-draft notice and kept-recording notice portals.
        The warning stays visible beside the gates; owner notices stay owned
        by their runtimes. */}
    <AppNoticeStack />
    <UnsavedRecordingWarning />
    {/* App-update line: inside NavStackProvider (it reads overlay depth) and
        outside auth/owner gates, so signed-out and recovery screens can
        update too. After the page for keyboard and screen-reader order;
        position: fixed keeps its visual position independent of that order. */}
    <UpdateReadyLine />
  </>;
}

export default function App() {
  return (
    <BrowserRouter
      // Deliver history changes to NavStackProvider's reconciler as urgent
      // updates. As transitions (the default), a PUSH followed by a POP before
      // the PUSH commits nets out in Router's location memo, so the reconciler
      // never runs and the layer and its close lock go stale (rule #78).
      useTransitions={false}
    >
      <NavStackProvider>
        <AppRoutes />
      </NavStackProvider>
    </BrowserRouter>
  );
}
